import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import CreditCards from './pages/CreditCards'
import Login from './pages/Login'
import ReferralLinks from './pages/ReferralLinks'
import NotFound from './pages/NotFound'
import ProtectedRoute from './components/ProtectedRoute'
import { ErrorBoundary, Skeleton } from './components/ui'

// Lazy-loaded routes — Recharts (Analytics) + the 1k-line MitcManagement
// dominate the bundle. Splitting them moves ~600KB out of the first-paint
// chunk; users only pay for it when they navigate to those pages.
const Analytics       = lazy(() => import('./pages/Analytics'))
const CardForm        = lazy(() => import('./pages/CardForm'))
const MitcManagement  = lazy(() => import('./pages/MitcManagement'))
const MitcRecordDetail = lazy(() => import('./pages/MitcRecordDetail'))
const Users           = lazy(() => import('./pages/Users'))
const UserDetail      = lazy(() => import('./pages/UserDetail'))
const OffersReview    = lazy(() => import('./pages/OffersReview'))
const OfferSources    = lazy(() => import('./pages/OfferSources'))
const NewsReview      = lazy(() => import('./pages/NewsReview'))
const AuditLog        = lazy(() => import('./pages/AuditLog'))
const ReferralAnalytics = lazy(() => import('./pages/ReferralAnalytics'))
const CostDashboard   = lazy(() => import('./pages/CostDashboard'))
const Grievances      = lazy(() => import('./pages/Grievances'))
const BreachNotice    = lazy(() => import('./pages/BreachNotice'))

function RouteLoading() {
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-surface-base">
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="users" element={<Users />} />
                  <Route path="users/:id" element={<UserDetail />} />
                  <Route path="cards" element={<CreditCards />} />
                  <Route path="cards/new" element={<CardForm />} />
                  <Route path="cards/:id/edit" element={<CardForm />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="mitc" element={<MitcManagement />} />
                  <Route path="mitc/:id" element={<MitcRecordDetail />} />
                  <Route path="offers" element={<OffersReview />} />
                  <Route path="offer-sources" element={<OfferSources />} />
                  <Route path="news" element={<NewsReview />} />
                  <Route path="referrals" element={<ReferralLinks />} />
                  <Route path="referral-analytics" element={<ReferralAnalytics />} />
                  <Route path="cost-dashboard" element={<CostDashboard />} />
                  <Route path="grievances" element={<Grievances />} />
                  <Route path="breach-notice" element={<BreachNotice />} />
                  <Route path="audit-log" element={<AuditLog />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
